// datoric — membership + cabinet/manager scaffold contracts.
//
// Membership is the ONLY grant path out of zero-access (ADR-0007): an
// authenticated user with no datoric_members row gets 403 no_access. Roles are
// read from D1, never from the client or a token claim.
import { z } from 'zod';

export const DatoricRoleSchema = z.enum(['participant', 'lead', 'manager']);
export type DatoricRole = z.infer<typeof DatoricRoleSchema>;

export const DatoricMemberStatusSchema = z.enum(['invited', 'active', 'paused', 'off']);
export type DatoricMemberStatus = z.infer<typeof DatoricMemberStatusSchema>;

// Latin-America collection scope. ISO-3166-1 alpha-2, uppercase — the launch
// set; widen here (single source of truth) rather than in a handler.
export const DATORIC_COUNTRIES = [
  'AR', 'BO', 'BR', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'GT',
  'HN', 'MX', 'NI', 'PA', 'PE', 'PY', 'SV', 'UY', 'VE',
] as const;
export const DatoricCountrySchema = z.enum(DATORIC_COUNTRIES);
export type DatoricCountry = z.infer<typeof DatoricCountrySchema>;

// UI languages for the region. Portuguese for BR, Spanish elsewhere; English is
// a staff/fallback language, never a collection language.
export const DatoricUiLangSchema = z.enum(['es', 'pt', 'en']);

// POST /api/datoric/_admin/member — machine class (X-Admin-Secret).
export const DatoricMemberUpsertSchema = z.object({
  uid: z.string().min(1),
  email: z.string().email(),
  role: DatoricRoleSchema.default('participant'),
  country: DatoricCountrySchema,
  status: DatoricMemberStatusSchema.default('invited'),
  ui_lang: DatoricUiLangSchema.default('es'),
});
export type DatoricMemberUpsert = z.infer<typeof DatoricMemberUpsertSchema>;

export const DatoricMemberUpsertResSchema = z.object({ ok: z.literal(true) });

// GET /api/datoric/cabinet/me — participant class.
export const DatoricCabinetMeResSchema = z.object({
  role: DatoricRoleSchema,
  country: z.string(),
  status: z.string(),
  ui_lang: z.string(),
  captures: z.number().int().nonnegative(),
});
export type DatoricCabinetMeRes = z.infer<typeof DatoricCabinetMeResSchema>;

// GET /api/datoric/manager/overview — manager class.
export const DatoricManagerOverviewResSchema = z.object({
  members: z.number().int().nonnegative(),
  captures: z.record(z.string(), z.number()),
});
export type DatoricManagerOverviewRes = z.infer<typeof DatoricManagerOverviewResSchema>;
